import type { z } from 'zod'
import { httpGet } from '@/shared/api/http'
import { env } from '@/shared/config/env'
import {
  cardSchema,
  dashboardStructureSchema,
  type Card,
  type DashboardStructure,
  type Timeframe,
} from './schemas'

const MANAGERIUM_BASE_URL = env.manageriumBaseUrl

const manageriumGet = <T extends z.ZodTypeAny>(path: string, schema: T) =>
  httpGet(MANAGERIUM_BASE_URL, path, schema)

/**
 * Okta-protected `GET /v1/dashboards/{id}`: the section/card layout. Calls
 * managerium with the Okta access token as a bearer.
 */
export const getDashboardStructure = (
  dashboardId: number
): Promise<DashboardStructure> =>
  manageriumGet(`/v1/dashboards/${dashboardId}`, dashboardStructureSchema)

export type CardFilters = {
  /**
   * The range to slice charts to, in months (read against the timeframe: -1 is
   * YTD's current year, 0 all time); left out, the whole series.
   */
  periods?: number | undefined
  /** The granularity, for cards that take it; left out, the backend uses Monthly. */
  timeframe?: Timeframe | undefined
  /** Market ids to keep; left out, every market. */
  markets?: string[] | undefined
  /** The deep dive the card is drawn in, so its settings for the card apply. */
  deepDive?: string | undefined
  /**
   * The name of the card's stub in that deep dive: picks which of the stubs
   * sharing one card id the request is for. Sent only with `deepDive`.
   */
  title?: string | undefined
  /** Regulatory deep-dive filters (C-level, criticality, tag); left out, no filtering. */
  cLevel?: string[] | undefined
  criticality?: string[] | undefined
  tag?: string[] | undefined
}

/**
 * Okta-protected `GET /v1/dashboards/{id}/cards/{cardId}`: one KPI or chart,
 * or an empty card when the market filter leaves nothing to show.
 */
export const getDashboardCard = (
  dashboardId: number,
  cardId: number,
  {
    periods,
    timeframe,
    markets,
    deepDive,
    title,
    cLevel,
    criticality,
    tag,
  }: CardFilters = {}
): Promise<Card> => {
  const query = new URLSearchParams()
  if (periods !== undefined) query.set('periods', String(periods))
  if (timeframe !== undefined) query.set('timeframe', timeframe)
  markets?.forEach(market => query.append('markets', market))
  if (deepDive !== undefined) query.set('deepDive', deepDive)
  if (title !== undefined) query.set('title', title)
  cLevel?.forEach(value => query.append('cLevel', value))
  criticality?.forEach(value => query.append('criticality', value))
  tag?.forEach(value => query.append('tag', value))
  const search = query.size > 0 ? `?${query}` : ''
  return manageriumGet(
    `/v1/dashboards/${dashboardId}/cards/${cardId}${search}`,
    cardSchema
  )
}
